import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { Location, NavigateFunction } from 'react-router';
import { useSelector } from 'react-redux';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { useGetAiReportsConfigQuery } from '#services/ai-reports';
import type { PanelItem, PanelContext } from '#addons/types';

const SECTION_ICONS: Record<string, SvgIconComponent> = {
  Home: HomeOutlinedIcon,
  Orders: ReceiptLongOutlinedIcon,
  Collections: CollectionsBookmarkOutlinedIcon,
  Products: Inventory2OutlinedIcon,
  'Receiving Management': MoveToInboxOutlinedIcon,
  'User Management': PeopleAltOutlinedIcon,
  'Warehouse Analysis': InsightsOutlinedIcon,
  'Website Management': LanguageOutlinedIcon,
  'Salesperson Report': BadgeOutlinedIcon,
  Promotions: LocalOfferOutlinedIcon,
  'Dummy Addon': ExtensionOutlinedIcon,
};

function getSectionIcon(label?: string) {
  const Icon = SECTION_ICONS[label ?? ''] || DashboardOutlinedIcon;
  return <Icon fontSize="small" />;
}

function matchesCurrent(data: PanelItem, pathname: string) {
  const aliases = ([] as Array<string | undefined>).concat(data.link, data.linkAlias).filter(Boolean) as string[];
  return aliases.some((u) => pathname.replace(/^\//, '').startsWith(u));
}

function containsActive(data: PanelItem, pathname: string): boolean {
  if (matchesCurrent(data, pathname)) return true;
  if (data.children) {
    return data.children.some((c) => containsActive(c, pathname));
  }
  return false;
}

interface MenuItemProps {
  data: PanelItem;
  depth: number;
  compact: boolean;
  onExpandSidebar: () => void;
  navigate: NavigateFunction;
  location: Location;
  userInfo: { role?: string } | null;
  panelContext: PanelContext;
}

function MenuItem(props: MenuItemProps) {
  const {
    data,
    depth,
    compact,
    onExpandSidebar,
    navigate,
    location,
    userInfo,
    panelContext,
  } = props;

  const hasChildren = Array.isArray(data.children) && data.children.length > 0;
  const active = matchesCurrent(data, location.pathname);
  const activeBranch = containsActive(data, location.pathname);

  const [open, setOpen] = useState(activeBranch || depth === 0);

  useEffect(() => {
    if (activeBranch) setOpen(true);
  }, [activeBranch]);

  if (!userInfo) return null;
  if (data.roles && !data.roles.includes(userInfo.role as string)) return null;
  if (typeof data.visibleWhen === 'function' && !data.visibleWhen(panelContext)) return null;

  const handleClick = (e: React.MouseEvent) => {
    if (hasChildren && compact && depth === 0) {
      e.preventDefault();
      onExpandSidebar();
      setOpen(true);
      return;
    }
    if (data.link) {
      e.preventDefault();
      void navigate(data.link, { state: { fromPanel: true } });
      return;
    }
    if (hasChildren) {
      setOpen((v) => !v);
    }
  };

  const iconNode = depth === 0
    ? getSectionIcon(data.label)
    : (
      <FiberManualRecordIcon
        sx={{
          fontSize: 6,
          color: active ? 'primary.main' : 'text.disabled',
        }}
      />
    );

  const label = data.badge ? (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {data.label}
      <Chip
        component="span"
        label={data.badge}
        size="small"
        color="warning"
        variant="outlined"
        sx={{
          height: 16,
          fontSize: '0.6rem',
          fontWeight: 600,
          '& .MuiChip-label': { px: 0.5, py: 0 },
          cursor: 'inherit',
        }}
      />
    </Box>
  ) : data.label;

  const button = (
    <ListItemButton
      component="a"
      data-testid={data.testid || ''}
      href={data.link || undefined}
      onClick={handleClick}
      disableRipple
      sx={(theme) => ({
        position: 'relative',
        mx: compact ? 0.75 : 1,
        my: 0.25,
        px: compact ? 0 : 1.25,
        py: depth === 0 ? 0.9 : 0.6,
        pl: compact ? 0 : 1.25 + depth * 1.25,
        borderRadius: 1.5,
        justifyContent: compact ? 'center' : 'flex-start',
        color: active ? 'primary.main' : 'text.primary',
        backgroundColor: active
          ? alpha(theme.palette.primary.main, 0.1)
          : 'transparent',
        transition: 'background-color 120ms ease, color 120ms ease',
        '&:hover': {
          backgroundColor: active
            ? alpha(theme.palette.primary.main, 0.14)
            : alpha(theme.palette.text.primary, 0.05),
        },
        '&::before': active ? {
          content: '""',
          position: 'absolute',
          left: compact ? 6 : 0,
          top: '22%',
          bottom: '22%',
          width: 3,
          borderRadius: 2,
          backgroundColor: 'primary.main',
        } : {},
      })}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: compact ? 0 : 1.25,
          justifyContent: 'center',
          color: active ? 'primary.main' : depth === 0 ? 'text.secondary' : 'inherit',
        }}
      >
        {iconNode}
      </ListItemIcon>
      {!compact && (
        <>
          <ListItemText
            primary={label}
            slotProps={{
              primary: {
                variant: 'body2',
                noWrap: true,
                sx: {
                  fontWeight: depth === 0 ? 600 : active ? 600 : 400,
                  fontSize: depth === 0 ? '0.875rem' : '0.825rem',
                },
              },
            }}
          />
          {hasChildren && (
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: 'text.secondary',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 180ms ease',
              }}
            />
          )}
        </>
      )}
    </ListItemButton>
  );

  const wrapped = compact && depth === 0 ? (
    <Tooltip title={data.label} placement="right" arrow>
      <Box>{button}</Box>
    </Tooltip>
  ) : button;

  return (
    <>
      {wrapped}
      {hasChildren && !compact && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {(data.children || []).map((child) => (
              <MenuItem
                key={child.label}
                data={child}
                depth={depth + 1}
                compact={compact}
                onExpandSidebar={onExpandSidebar}
                navigate={navigate}
                location={location}
                userInfo={userInfo}
                panelContext={panelContext}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

interface AdminPanelProps {
  panel: PanelItem[];
  compact?: boolean;
  onExpandSidebar?: () => void;
}

export default function AdminPanel(props: AdminPanelProps) {
  const { panel, compact = false, onExpandSidebar = () => {} } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const userInfo = useSelector((state: { auth: { userInfo: { role?: string } | null } }) => state.auth.userInfo);
  const { data: aiReportsConfig } = useGetAiReportsConfigQuery(undefined);

  const panelContext = useMemo<PanelContext>(
    () => ({ aiReportsEnabled: (aiReportsConfig as { enabled?: boolean } | undefined)?.enabled }),
    [aiReportsConfig],
  );

  return (
    <List
      component="nav"
      aria-label="Admin navigation"
      disablePadding
      sx={{ py: 1 }}
    >
      {panel.map((item) => (
        <MenuItem
          key={item.label}
          data={item}
          depth={0}
          compact={compact}
          onExpandSidebar={onExpandSidebar}
          navigate={navigate}
          location={location}
          userInfo={userInfo}
          panelContext={panelContext}
        />
      ))}
    </List>
  );
}
